<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton v-show="currentTab === 1" class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
            <NevsButton class="error" @click="backClick"><i class="fa-solid fa-arrow-left"></i>
                {{ $LANG.Get('buttons.back') }}
            </NevsButton>
            <NevsButton v-if="mode === 'edit' && sessionInFuture" v-show="currentTab === 1" class="error delete-button"
                        @click="deleteClick()"><i class="fa-solid fa-trash"></i>
                {{ $LANG.Get('buttons.delete') }}
            </NevsButton>
            <span v-if="mode === 'edit' && currentTab !== 1 && hasUnsavedChanges" class="unsaved-changes">
                {{ $LANG.Get('labels.unsavedChanges') }} {{ $LANG.Get('buttons.generalData') }}
            </span>
        </div>

        <div class="tabs">
            <NevsButton :class="{'active' : currentTab === 1}" @click="currentTab = 1"><i class="fa-solid fa-address-card"></i> 
                {{ $LANG.Get('buttons.generalData') }}
            </NevsButton>
            <NevsButton v-if="mode === 'edit'" :class="{'active' : currentTab === 2}" @click="currentTab = 2"><i class="fa-solid fa-users"></i>
                {{ $LANG.Get('buttons.attendees') }}
            </NevsButton>
            <NevsButton v-if="mode === 'edit'" :class="{'active' : currentTab === 3}" @click="currentTab = 3"><i class="fa-solid fa-hourglass-half"></i>
                {{ $LANG.Get('buttons.waitingList') }}
            </NevsButton>
        </div>

        <div v-show="currentTab === 1" class="tab-body">
            <NevsCard class="form form-float">
                <NevsSelect v-model="session.session_type_id" :ajax="'select/session-types'" :error="validation.session_type_id"
                            :label="'*' + $LANG.Get('fields.sessionType') + ':'"></NevsSelect>
                <NevsSelect v-model="session.room_id" :ajax="'select/rooms'" :error="validation.room_id"
                            :label="'*' + $LANG.Get('fields.room') + ':'"></NevsSelect>
                <div v-if="capacityWarning" class="capacity-warning">
                    <i class="fa-solid fa-triangle-exclamation"></i> {{ $LANG.Get('labels.roomCannotAccommodate') }}
                </div>
                <NevsSelect v-model="session.trainer_id" :ajax="'select/users'" :error="validation.trainer_id"
                            :label="'*' + $LANG.Get('fields.trainer') + ':'"></NevsSelect>
                <NevsCheckbox v-if="mode === 'add'" v-model="session.repeatable" :label="$LANG.Get('fields.repeatable')"></NevsCheckbox>
            </NevsCard>
            <div class="nevs-clear-float"></div>

            <NevsCard v-if="!session.repeatable" class="form">
                <h3 class="session-section-heading">{{ $LANG.Get('labels.sessionTime') }}</h3>
                <NevsDateField v-model="session.date" :error="validation.date"
                               :label="'*' + $LANG.Get('fields.date') + ':'"></NevsDateField>
                <NevsMaskedField v-model="session.start_time" :mask="'NN:NN'" :error="validation.start_time"
                                 :label="'*' + $LANG.Get('fields.startTime') + ':'" class="session-half"></NevsMaskedField>
                <NevsMaskedField v-model="session.end_time" :mask="'NN:NN'" :error="validation.end_time"
                                 :label="'*' + $LANG.Get('fields.endTime') + ':'" class="session-half"></NevsMaskedField>
                <div class="nevs-clear-float"></div>
            </NevsCard>

            <NevsCard v-if="session.repeatable" class="form schedule-card">
                <h3 class="session-section-heading">{{ $LANG.Get('labels.repeatableSettings') }}</h3>
                <div class="repeatable-disclaimer">
                    <i class="fa-solid fa-circle-info"></i> {{ $LANG.Get('labels.repeatableDisclaimer') }}
                </div>
                <NevsDateField v-model="session.repeatable_start_date" :error="validation.repeatable_start_date"
                               :label="'*' + $LANG.Get('fields.repeatableStartDate') + ':'" class="session-half"></NevsDateField>
                <NevsDateField v-model="session.repeatable_end_date" :error="validation.repeatable_end_date"
                               :label="'*' + $LANG.Get('fields.repeatableEndDate') + ':'" class="session-half"></NevsDateField>
                <div class="nevs-clear-float"></div>

                <h3 class="session-section-heading session-schedule-heading">{{ $LANG.Get('labels.weeklySchedule') }}</h3>
                <div v-if="validation.repeatable_data" class="box-error nevs-text-error">{{ validation.repeatable_data }}</div>
                <table class="repeatable-table">
                    <thead>
                        <tr>
                            <th>{{ $LANG.Get('fields.weekday') }}</th>
                            <th style="width: 160px;">{{ $LANG.Get('fields.startTime') }}</th>
                            <th style="width: 160px;">{{ $LANG.Get('fields.endTime') }}</th>
                            <th style="width: 20px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(entry, key) in session.repeatable_data" :key="key">
                            <td><NevsSelect v-model="entry.weekday" :options="weekdayOptions"></NevsSelect></td>
                            <td><NevsMaskedField v-model="entry.start" :mask="'NN:NN'"></NevsMaskedField></td>
                            <td><NevsMaskedField v-model="entry.end" :mask="'NN:NN'"></NevsMaskedField></td>
                            <td class="repeatable-table-actions"><span :title="$LANG.Get('tooltips.delete')" class="session-button" @click="removeEntry(key)"><i
                                class="fa-solid fa-trash"></i></span></td>
                        </tr>
                    </tbody>
                </table>
                <NevsButton style="margin-top: 10px;" @click="addEntry()"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
            </NevsCard>
        </div>

        <div v-if="mode === 'edit'" v-show="currentTab === 2" class="tab-body">
            <NevsCard class="form">
                <h3 class="session-section-heading">
                    {{ $LANG.Get('labels.attendance') }}:
                    <span :class="{'attendance-over': overCapacity}">{{ attendees.length }} / {{ attendeeCapacity }}</span>
                </h3>
                <div v-if="overCapacity" class="capacity-warning">
                    <i class="fa-solid fa-triangle-exclamation"></i> {{ $LANG.Get('labels.overCapacity') }}
                </div>

                <div class="attendees-add">
                    <NevsSelect :nullable="true" v-model="selectedPersonId" :ajax="'select/persons'"
                                :label="$LANG.Get('fields.person') + ':'" class="attendees-add-field"></NevsSelect>
                    <NevsButton class="primary attendees-add-button" @click="addPerson()"><i class="fa-solid fa-plus"></i> 
                        {{ $LANG.Get('buttons.add') }}
                    </NevsButton>
                </div>
                <NevsSelect :nullable="true" v-model="selectedGroupId" :ajax="'select/groups'"
                            :label="$LANG.Get('labels.addFromGroup') + ':'" class="attendees-add-field"></NevsSelect>

                <table class="attendees-table">
                    <thead>
                        <tr>
                            <th>{{ $LANG.Get('fields.person') }}</th>
                            <th style="width: 20px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="attendee in attendees" :key="attendee.id">
                            <td>
                                {{ attendee.person_name }}
                                <span v-if="attendee.missing_membership" class="attendee-missing-membership"
                                      :title="$LANG.Get('labels.missingMembership')">
                                    <i class="fa-solid fa-triangle-exclamation"></i> {{ $LANG.Get('labels.missingMembership') }}
                                </span>
                            </td>
                            <td class="attendees-table-action"><span :title="$LANG.Get('tooltips.delete')" class="session-button"
                                  @click="removeAttendee(attendee)"><i class="fa-solid fa-trash"></i></span></td>
                        </tr>
                        <tr v-if="attendees.length === 0">
                            <td colspan="2" class="attendees-empty">{{ $LANG.Get('labels.noAttendees') }}</td>
                        </tr>
                    </tbody>
                </table>
            </NevsCard>
        </div>

        <div v-if="mode === 'edit'" v-show="currentTab === 3" class="tab-body">
            <NevsCard class="form">
                <h3 class="session-section-heading">
                    {{ $LANG.Get('buttons.waitingList') }}: <span>{{ waitingList.length }}</span>
                </h3>

                <div class="attendees-add">
                    <NevsSelect :nullable="true" v-model="selectedWaitingPersonId" :ajax="'select/persons'"
                                :label="$LANG.Get('fields.person') + ':'" class="attendees-add-field"></NevsSelect>
                    <NevsButton class="primary attendees-add-button" @click="addToWaitingList()"><i class="fa-solid fa-plus"></i>
                        {{ $LANG.Get('buttons.add') }}
                    </NevsButton>
                </div>

                <table class="attendees-table">
                    <thead>
                        <tr>
                            <th style="width: 40px;">#</th>
                            <th>{{ $LANG.Get('fields.person') }}</th>
                            <th style="width: 20px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="entry in waitingList" :key="entry.id">
                            <td>{{ entry.position }}</td>
                            <td>{{ entry.person_name }}</td>
                            <td class="attendees-table-action"><span :title="$LANG.Get('tooltips.delete')" class="session-button"
                                  @click="removeFromWaitingList(entry)"><i class="fa-solid fa-trash"></i></span></td>
                        </tr>
                        <tr v-if="waitingList.length === 0">
                            <td colspan="3" class="attendees-empty">{{ $LANG.Get('labels.noWaitingList') }}</td>
                        </tr>
                    </tbody>
                </table>
            </NevsCard>
        </div>

        <div v-if="showGroupDeleteDialog" class="group-delete-overlay">
            <div class="group-delete-dialog">
                <div class="group-delete-text">{{ $LANG.Get('alerts.sessionGroupDeleteQuestion') }}</div>
                <div class="group-delete-buttons">
                    <NevsButton class="primary" @click="performDelete('single')"><i class="fa-solid fa-trash"></i>
                        {{ $LANG.Get('buttons.deleteThisSession') }}
                    </NevsButton>
                    <NevsButton class="error" @click="performDelete('group')"><i class="fa-solid fa-trash"></i>
                        {{ $LANG.Get('buttons.deleteWholeGroup') }}
                    </NevsButton>
                    <NevsButton @click="showGroupDeleteDialog = false"><i class="fa-solid fa-xmark"></i>
                        {{ $LANG.Get('buttons.cancel') }}
                    </NevsButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Session from '@/models/Session.js';
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsMaskedField from "@/components/nevs/NevsMaskedField.vue";

export default {
    name: "EntitySession",
    components: {NevsMaskedField, NevsDateField, NevsCheckbox, NevsSelect, NevsButton, NevsCard},
    data() {
        return {
            loaded: false,
            mode: this.$route.params.id === '0' ? 'add' : 'edit',
            currentTab: 1,
            session: new Session({}),
            pristineSession: null,
            roomCapacity: null,
            sessionTypeCapacity: null,
            attendees: [],
            attendeeCapacity: 0,
            selectedPersonId: null,
            selectedGroupId: null,
            waitingList: [],
            selectedWaitingPersonId: null,
            inGroup: false,
            showGroupDeleteDialog: false,
            weekdayOptions: this.$HELPERS.GetWeekdays(this),
            validation: {
                session_type_id: '',
                room_id: '',
                trainer_id: '',
                date: '',
                start_time: '',
                end_time: '',
                repeatable_data: '',
                repeatable_start_date: '',
                repeatable_end_date: ''
            }
        }
    },
    computed: {
        capacityWarning() {
            return this.roomCapacity !== null && this.sessionTypeCapacity !== null
                && this.roomCapacity < this.sessionTypeCapacity;
        },
        overCapacity() {
            return this.attendeeCapacity > 0 && this.attendees.length > this.attendeeCapacity;
        },
        hasUnsavedChanges() {
            if (this.pristineSession === null) return false;
            return JSON.stringify(this.session) !== this.pristineSession;
        },
        sessionInFuture() {
            if (this.session.date === null || this.session.date === '' || this.session.start_time === '') return false;
            let start = new Date(this.session.date + 'T' + this.session.start_time + ':00');
            if (isNaN(start.getTime())) return false;
            return start.getTime() >= Date.now();
        }
    },
    watch: {
        'session.room_id'() {
            this.loadRoomCapacity();
        },
        'session.session_type_id'() {
            this.loadSessionTypeCapacity();
        },
        selectedGroupId(value) {
            // A group is applied as soon as it is picked: all of its persons are added to the session.
            if (value !== null) {
                this.addGroup(value);
            }
        }
    },
    methods: {
        loadRoomCapacity() {
            if (this.session.room_id === null) {
                this.roomCapacity = null;
                return;
            }
            let vm = this;
            this.$API.APICall('get', 'rooms/' + this.session.room_id, {}, (data, success) => {
                if (success && data.room) {
                    vm.roomCapacity = data.room.capacity;
                }
            });
        },
        loadSessionTypeCapacity() {
            if (this.session.session_type_id === null) {
                this.sessionTypeCapacity = null;
                return;
            }
            let vm = this;
            this.$API.APICall('get', 'session-types/' + this.session.session_type_id, {}, (data, success) => {
                if (success && data.session_type) {
                    vm.sessionTypeCapacity = data.session_type.capacity;
                }
            });
        },
        loadAttendees() {
            let vm = this;
            this.$API.APICall('get', 'person-sessions/by-session/' + this.session.id, {}, (data, success) => {
                if (success) {
                    vm.applyAttendees(data);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        applyAttendees(data) {
            this.attendees = data.records;
            this.attendeeCapacity = data.capacity;
        },
        loadWaitingList() {
            let vm = this;
            this.$API.APICall('get', 'session-waiting-list/by-session/' + this.session.id, {}, (data, success) => {
                if (success) {
                    vm.waitingList = data.records;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        addToWaitingList() {
            if (this.selectedWaitingPersonId === null) return;
            let vm = this;
            let payload = {session_id: this.session.id, person_id: this.selectedWaitingPersonId};
            this.$API.APICall('post', 'session-waiting-list', payload, (data, success) => {
                if (success) {
                    vm.waitingList = data.records;
                    vm.selectedWaitingPersonId = null;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        removeFromWaitingList(entry) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.$API.APICall('delete', 'session-waiting-list/' + entry.id, {}, (data, success) => {
                            if (success) {
                                vm.waitingList = data.records;
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        addPerson() {
            if (this.selectedPersonId === null) return;
            let vm = this;
            let payload = {session_id: this.session.id, person_id: this.selectedPersonId};
            this.$API.APICall('post', 'person-sessions', payload, (data, success) => {
                if (success) {
                    vm.applyAttendees(data);
                    vm.selectedPersonId = null;
                    // Registering a person also drops them from the waiting list, so refresh it.
                    vm.loadWaitingList();
                    vm.$CROSS_TAB_BUS.TriggerEvent('reload-calendar', {});
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        addGroup(groupId) {
            let vm = this;
            let payload = {session_id: this.session.id, group_id: groupId};
            this.$API.APICall('post', 'person-sessions/by-group', payload, (data, success) => {
                vm.selectedGroupId = null;
                if (success) {
                    vm.applyAttendees(data);
                    vm.loadWaitingList();
                    vm.$CROSS_TAB_BUS.TriggerEvent('reload-calendar', {});
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        removeAttendee(attendee) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.$API.APICall('delete', 'person-sessions/' + attendee.id, {}, (data, success) => {
                            if (success) {
                                vm.applyAttendees(data);
                                // Removing an attendee may have promoted the first waiting-list person.
                                vm.loadWaitingList();
                                vm.$CROSS_TAB_BUS.TriggerEvent('reload-calendar', {});
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        deleteClick() {
            // Sessions that belong to a repeating group let the user choose between deleting only this
            // occurrence or the whole group; standalone sessions get a plain confirmation.
            if (this.inGroup) {
                this.showGroupDeleteDialog = true;
                return;
            }
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.sessionDeletionQuestion'), callback: (response) => {
                    if (response) {
                        vm.performDelete('single');
                    }
                }
            });
        },
        performDelete(scope) {
            this.showGroupDeleteDialog = false;
            let vm = this;
            this.$API.APICall('delete', 'sessions/' + this.session.id + '/' + scope, {}, (data, success) => {
                if (success) {
                    vm.$CROSS_TAB_BUS.TriggerEvent('reload-calendar', {});
                    vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordDeleted')});
                    vm.backClick();
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        addEntry() {
            this.session.repeatable_data.push({weekday: 1, start: '09:00', end: '10:00'});
        },
        removeEntry(index) {
            this.session.repeatable_data.splice(index, 1);
        },
        backClick() {
            if (window.history.length === 1) {
                window.close();
            } else {
                this.$router.back();
            }
        },
        clearValidation() {
            for (let key in this.validation) {
                this.validation[key] = '';
            }
        },
        saveClick() {
            this.clearValidation();
            let valid = true;
            let mandatory = this.$LANG.Get('labels.mandatoryField');
            let timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;

            if (this.session.session_type_id === null) {
                valid = false;
                this.validation.session_type_id = mandatory;
            }
            if (this.session.room_id === null) {
                valid = false;
                this.validation.room_id = mandatory;
            }
            if (this.session.trainer_id === null) {
                valid = false;
                this.validation.trainer_id = mandatory;
            }

            if (this.session.repeatable) {
                if (this.session.repeatable_data.length === 0) {
                    valid = false;
                    this.validation.repeatable_data = this.$LANG.Get('labels.repeatableDataRequired');
                } else {
                    for (let entry of this.session.repeatable_data) {
                        if (!timePattern.test(entry.start) || !timePattern.test(entry.end)) {
                            valid = false;
                            this.validation.repeatable_data = this.$LANG.Get('labels.invalidTime');
                            break;
                        }
                    }
                }
                if (this.session.repeatable_start_date === null || this.session.repeatable_start_date === '') {
                    valid = false;
                    this.validation.repeatable_start_date = mandatory;
                }
                if (this.session.repeatable_end_date === null || this.session.repeatable_end_date === '') {
                    valid = false;
                    this.validation.repeatable_end_date = mandatory;
                }
            } else {
                if (this.session.date === null || this.session.date === '') {
                    valid = false;
                    this.validation.date = mandatory;
                }
                if (!timePattern.test(this.session.start_time)) {
                    valid = false;
                    this.validation.start_time = this.$LANG.Get('labels.invalidTime');
                }
                if (!timePattern.test(this.session.end_time)) {
                    valid = false;
                    this.validation.end_time = this.$LANG.Get('labels.invalidTime');
                }
            }

            if (this.capacityWarning) {
                valid = false;
                this.validation.room_id = this.$LANG.Get('labels.roomCannotAccommodate');
            }

            if (!valid) {
                return;
            }

            let vm = this;
            let method = this.mode === 'add' ? 'post' : 'put';
            let action = this.mode === 'add' ? 'sessions' : 'sessions/' + this.session.id;
            this.$API.APICall(method, action, this.session, (data, success) => {
                if (success) {
                    vm.$CROSS_TAB_BUS.TriggerEvent('reload-calendar', {});
                    vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                    vm.backClick();
                } else if (data.room_id === 'insufficient_capacity') {
                    vm.validation.room_id = vm.$LANG.Get('labels.roomCannotAccommodate');
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'training');
        this.$store.commit('selectSubMenu', 'calendar');

        if (this.mode === 'add') {
            this.$store.commit('setBreadcrumbs', [
                {label: this.$LANG.Get('modules.training'), link: null},
                {label: this.$LANG.Get('modules.calendar'), link: '/calendar'},
                {label: this.$LANG.Get('modules.newSession'), link: null}
            ]);
            this.loaded = true;
        } else {
            let vm = this;
            this.$API.APICall('get', 'sessions/' + this.$route.params.id, {}, (data, success) => {
                if (success && data.session !== null) {
                    vm.session.Fill(data.session);
                    if (!Array.isArray(vm.session.repeatable_data)) vm.session.repeatable_data = [];
                    vm.inGroup = data.session.in_group === true;
                    vm.pristineSession = JSON.stringify(vm.session);
                    vm.$store.commit('setBreadcrumbs', [
                        {label: vm.$LANG.Get('modules.training'), link: null},
                        {label: vm.$LANG.Get('modules.calendar'), link: '/calendar'},
                        {label: data.session.session_type_name, link: null}
                    ]);
                    vm.loaded = true;
                    vm.loadAttendees();
                    vm.loadWaitingList();
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped>
.form {
    max-width: 600px;
    margin-bottom: 10px;
    padding: 15px;
}

.form-float {
    float: left;
    margin-right: 10px;
    max-width: 400px;
}

.session-half {
    width: 50% !important;
    float: left;
}

.session-section-heading {
    margin: 0 0 10px;
    font-size: 16px;
}

.session-schedule-heading {
    margin-top: 20px;
}

.capacity-warning {
    margin: 0 0 10px;
    padding: 8px 10px;
    border-radius: 5px;
    background: #fdecec;
    border: 1px solid #f3b4b4;
    color: #b00020;
    font-size: 13px;
}

.repeatable-disclaimer {
    margin: 0 0 10px;
    padding: 8px 10px;
    border-radius: 5px;
    background: #e8f1fb;
    border: 1px solid #b5d3f0;
    color: #1c4e80;
    font-size: 13px;
}

.box-error {
    font-size: 12px;
    margin-bottom: 5px;
}

.repeatable-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
}

.repeatable-table th {
    text-align: left;
    font-size: 12px;
    padding: 0 5px;
}

.repeatable-table td {
    padding: 0 5px;
    vertical-align: top;
}

.repeatable-table td.repeatable-table-actions {
    vertical-align: middle;
}

.session-button {
    margin-right: 10px;
    font-size: 14px;
    cursor: pointer;
}

.tabs {
    margin-bottom: 10px;
    border: 2px #b7bcc0 solid;
    border-bottom: none;
    padding: 5px;
    background: #ffd496;
    border-radius: 5px 5px 0 0;
}

.tabs .nevs-button {
    border-radius: 0;
    margin-right: 10px;
    background: none;
    color: #5e6278;
    padding-bottom: 2px;
}

.tabs .nevs-button.active {
    border-bottom: 2px dotted #5e6278;
}

.tab-body {
    padding: 10px;
    margin-top: -10px;
    border: 2px #b7bcc0 solid;
    border-radius: 0 0 5px 5px;
}

.tab-body::after {
    content: "";
    display: block;
    clear: both;
}

.unsaved-changes {
    margin-left: 10px;
    color: #b00020;
    font-size: 13px;
}

.delete-button {
    margin-left: 10px;
}

.group-delete-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.group-delete-dialog {
    background: #fff;
    border-radius: 5px;
    padding: 20px;
    max-width: 420px;
    width: 90%;
    box-shadow: 0 5px 25px rgba(0, 0, 0, 0.25);
}

.group-delete-text {
    margin-bottom: 15px;
    font-size: 14px;
}

.group-delete-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.attendance-over {
    color: #b00020;
    font-weight: bold;
}

.attendees-add {
    display: flex;
    align-items: center;
}

.attendees-add-field {
    max-width: 400px;
}

/* The select carries a label above its input, so offset the button down by the label's height to
   centre it against the dropdown itself rather than the whole labelled field. */
.attendees-add-button {
    margin-top: 21px;
}

.attendees-table {
    width: 100%;
    max-width: 600px;
    border-collapse: collapse;
    margin-top: 15px;
}

.attendees-table th {
    text-align: left;
    font-size: 12px;
    padding: 5px;
    border-bottom: 1px solid grey;
}

.attendees-table td {
    padding: 8px 5px;
    border-bottom: 1px solid #e3e7ee;
}

.attendees-table td.attendees-table-action {
    width: 20px;
    vertical-align: middle;
    text-align: center;
}

.attendees-empty {
    color: #99a1b3;
    font-style: italic;
}

.attendee-missing-membership {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 12px;
    background: #fdecec;
    border: 1px solid #f3b4b4;
    color: #b00020;
    font-size: 12px;
    white-space: nowrap;
}
</style>
